import { defineStore } from 'pinia';
import {
  InstitutionDepartment,
  GetDepartmentFragment,
} from '@/graphql/custom-types';
import { Module_List, Profile_Type } from '@/graphql/generated-schema-types';
import api from '@/api';
import { getModuleData } from '@/utils/modules';
import { useGeneralStore } from '@/stores/general';
import { useInstitutionStore } from '@/stores/institution';
import * as Sentry from '@sentry/vue';
import { ModuleList } from '@/graphql/shared-types';

export interface IDepartmentModule {
  type: ModuleList;
  name: string;
  colorId: string;
  colorHex: string;
  icon: string;
  row: { start: string; end: string };
  col: { start: string; end: string };
  frHeight: number;
  frWidth: number;
}

type IDepartmentState = GetDepartmentFragment & {
  modules: IDepartmentModule[];
  hasValidExternalModule: boolean | null;
  institutionId: string | null;
  weatherForecast: string | null;
  archive: object;
};

export const useDepartmentStore = defineStore('department', {
  state: (): IDepartmentState => ({
    loginKey: '',
    color: '',
    icon: '',
    id: '',
    hasValidExternalModule: null,
    institutionId: null,
    presentation: {
      description: '',
      image: '',
    },
    name: '',
    settings: {
      shift: {
        daysForward: null,
      },
      meal: {
        daysForward: 0,
        nextDay: '',
        signup: false,
      },
      screenConfig: {
        footerConfig: [],
        carouselDuration: 0,
        slideshowDuration: 0,
        timeout: 0,
        gridSize: 0,
        departmentSwitcher: false,
        modules: [],
      },
      simpleSchedulePlan: [],
    },
    weatherForecast: null,
    archive: {},
    modules: [],
  }),
  actions: {
    checkIfValidExternalModule() {
      const institutionStore = useInstitutionStore();

      if (!institutionStore.didLoadExternalConnections) return;

      const hasExternalActivitiesModule =
        this.settings?.screenConfig?.footerConfig?.includes(
          'EXTERNAL_ACTIVITIES' as Module_List
        );

      const hasExternalConnections =
        !!institutionStore.externalConnections?.length;

      this.hasValidExternalModule = !!(
        hasExternalActivitiesModule && hasExternalConnections
      );
    },

    async getDepartment() {
      if (!this.id) {
        throw new Error('No department id provided');
      }

      try {
        const department = await api.department.getDepartment(this.id);
        const moduleData = getModuleData();

        const modules: IDepartmentModule[] = (
          department.settings?.screenConfig?.modules || []
        )
          .map((module) => {
            if (!module || !module.type) {
              return null;
            }

            const rowValues = (module?.rowSpan || '').split('/');
            const colValues = (module?.colSpan || '').split('/');
            const name =
              moduleData[module.type].name || moduleData[module.type].component;

            return {
              type: module.type as unknown as ModuleList,
              name,
              colorId: moduleData[module.type].colorId,
              colorHex: moduleData[module.type].colorHex,
              icon: moduleData[module.type].icon,
              row: {
                start: rowValues[0],
                end: rowValues[1],
              },
              col: {
                start: colValues[0],
                end: colValues[1],
              },
              frHeight: Number(rowValues[1]) - Number(rowValues[0]),
              frWidth: Number(colValues[1]) - Number(colValues[0]),
            };
          })
          .filter((item) => !!item);

        this.$patch({
          ...department,
          modules,
        });

        this.checkIfValidExternalModule();
      } catch (error) {
        console.log('Error getting department', error);
      } finally {
        Sentry.setUser({
          id: this.id,
          departmentSettings: this.settings,
          institutionId: this.institutionId,
        });
      }
    },
    async setDepartmentId(id: string) {
      this.id = id;
    },
    async switchDepartment(department: InstitutionDepartment | null) {
      const generalStore = useGeneralStore();
      generalStore.setLoading(true);

      if (!department) {
        sessionStorage.removeItem('temp_login_key');
        sessionStorage.removeItem('temp_departments');
      } else {
        sessionStorage.setItem('temp_login_key', department?.loginKey || '');
      }

      window.localStorage.clear();
      window.location.href = '/';
    },
    async getWeather() {
      try {
        this.weatherForecast = await api.department.getDepartmentWeather(
          this.id
        );
      } catch (error) {
        console.error('Error fetching weather', error);
      }
    },
    async getArchive(id: string) {
      try {
        const archive = await api.archive.getMyArchive({
          profileType: Profile_Type.Department,
          profileId: id,
        });
        this.archive = archive;
      } catch (error) {
        console.log('Error getting my archive', error);
      }
    },
  },
});
